import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';

import type { NativeEmbedChildCommand } from '../commands';
import type { NativeEmbedFeature } from '../features/main';
import type { UiConfig } from '../ui-config';
import type { URLMetaData } from '../url/types';

export type NativeEmbedClientId = ClientId;

/**
 * View mode for the embed container. Aligned with parent→child view-mode-change events.
 */
export type NativeEmbedViewMode = 'regular' | 'maximize';

/**
 * Branded type for experience manifest IDs. Use {@link experienceManifestId} to create.
 */
export type ExperienceManifestId = string & { __experienceManifestId: symbol };

/**
 * Converts a string to an {@link ExperienceManifestId}.
 */
export const experienceManifestId = (s: string): ExperienceManifestId => s as ExperienceManifestId;

/**
 * DTO for creating an experience: manifest plus resolved URL metadata.
 * Used by the facade (which resolves the URL once) and passed to the experience service.
 */
export type ExperienceCreateDto = {
	manifest: ExperienceManifest;
	urlMeta: URLMetaData;
};

/**
 * Manifest for registering an experience.
 */
export type ExperienceManifest = {
	/**
	 * Allows for handling child commands for the embed has sent back.
	 * This is used to extend the experience with additional functionality.
	 */
	handleChildCommands: (experience: Experience, commands: NativeEmbedChildCommand) => void;
	id: ExperienceManifestId;
	isActive: (urlMeta: URLMetaData) => boolean;
	isUrlMatch: (url: string) => boolean;
	/**
	 * Features this experience requests during the host↔child handshake.
	 *
	 * If this set includes `disable-child-scroll`, the host surface **must**
	 * register a surface with a scroll handler via `facade.setSurface()`.
	 * Otherwise scroll events from the child will be silently dropped.
	 *
	 * @see {@link SurfaceService}
	 */
	supportedFeatures: Set<NativeEmbedFeature>;
	uiConfig: UiConfig;
};

/**
 * Host-side instance for a single native embed. One instance per embed; `id` uniquely
 * identifies it.
 *
 * Used to be passed down through the React components to specify the experience.
 *
 * Obtained by calling `getExperienceService().registerExperience(manifest)` with an
 * {@link ExperienceManifest}. Each registered embed gets its own Experience instance.
 */
export interface Experience {
	id: NativeEmbedClientId;
	/**The experience decides if it wants to handle this native embed URL based on the URL metadata */
	isActive: (urlMeta: URLMetaData) => boolean;
	/** The experience decides if it wants to handle this native embed URL based on the URL metadata */
	isUrlMatch: (urlMeta: string) => boolean;
	metaData?: URLMetaData;
	supportedFeatures: Promise<Set<NativeEmbedFeature>>;
	uiConfig: UiConfig;
}
